import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createVNode as _createVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, renderList as _renderList, Fragment as _Fragment, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, normalizeClass as _normalizeClass, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createStaticVNode as _createStaticVNode } from "vue"

const _hoisted_1 = { class: "site-footer__inner" }
const _hoisted_2 = { class: "site-footer__top" }
const _hoisted_3 = {
  class: "site-footer__brand",
  "aria-labelledby": "site-footer-title"
}
const _hoisted_4 = {
  class: "site-footer__aside",
  "aria-label": "Ключевые элементы доверия"
}
const _hoisted_5 = { class: "site-footer__trust" }
const _hoisted_6 = { class: "site-footer__nav-grid" }
const _hoisted_7 = ["aria-label"]
const _hoisted_8 = {
  class: "site-footer__nav-details",
  open: ""
}
const _hoisted_9 = { class: "site-footer__nav-title" }
const _hoisted_10 = { class: "site-footer__nav-list" }
const _hoisted_11 = ["href", "target", "rel"]
const _hoisted_12 = { class: "site-footer__bottom" }
const _hoisted_13 = { class: "site-footer__company" }

import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import SvgLogo from '../common/SvgLogo.vue';

type FooterLink = {
    label: string;
    to?: string;
    href?: string;
    external?: boolean;
    // Акцентный пункт списка: рисуется как CTA, а не как обычная ссылка.
    emphasis?: boolean;
};

type FooterGroup = {
    title: string;
    ariaLabel: string;
    links: FooterLink[];
};

// Каталог наставников (/AllMentors) временно доступен только админам, поэтому
// футер-ссылки на него (лого-линк и пункт «Все наставники») гейтятся признаком
// isAdmin. Значение прокидывает App.vue (единый источник — useIsAdmin), футер
// остаётся презентационным. Дефолт false ⇒ по умолчанию каталог скрыт.
const REVEAL_SCROLL_DAMPING = 0.82;
const REVEAL_TRIGGER_TOLERANCE_PX = 2;
const DEFAULT_FOOTER_PEEK_HEIGHT = 28;
const WHEEL_REVEAL_QUIET_GAP_MS = 220;
const WHEEL_REVEAL_LOCK_MS = 160;
const WHEEL_REVEAL_MIN_DELTA = 18;
const WHEEL_REVEAL_DELTA_ACCELERATION_PX = 8;
const WHEEL_REVEAL_DELTA_ACCELERATION_RATIO = 1.28;
const KEY_TOUCH_REVEAL_LOCK_MS = 260;
const STOP_NUDGE_DURATION_MS = 460;
const BODY_SCROLL_LOCK_CLASS = 'body-scroll-locked';

export default /*@__PURE__*/_defineComponent({
  __name: 'FooterComponent',
  props: {
    isAdmin: { type: Boolean, default: false }
  },
  setup(__props: any) {

const props = __props;

const supportUserEnabled =
    String((globalThis as { process?: { env?: Record<string, string | undefined> } })
        .process?.env?.VUE_APP_SUPPORT_REPORT_BUTTON_ENABLED ?? '').toLowerCase() === 'true';

// Лого в футере вело в каталог наставников. Для не-админа каталог скрыт →
// уводим на главную (аутентифицированного корень сам перебросит на /dashboard).
const brandLink = computed<string>(() => (props.isAdmin ? '/AllMentors' : '/'));

const currentYear = computed(() => new Date().getFullYear());
const route = useRoute();
const footerRef = ref<HTMLElement | null>(null);
const footerHeight = ref(720);
const revealActive = ref(false);
const revealProgress = ref(0);
const stopNudgeActive = ref(false);
const bodyScrollLocked = ref(false);
let resizeObserver: ResizeObserver | null = null;
let bodyClassObserver: MutationObserver | null = null;
let animationFrame = 0;
let lastTouchY = 0;
let footerRevealArmed = false;
let footerRevealReadyAt = 0;
let lastWheelDeltaAbs = 0;
let lastWheelEventAt = 0;
let stopNudgeFrame = 0;
let stopNudgeTimeout = 0;
let touchRevealBlockedUntilRelease = false;

const WHEEL_EVENT_OPTIONS: AddEventListenerOptions = { passive: false, capture: true };
const MODAL_REVEAL_BLOCK_SELECTOR = [
    '[aria-modal="true"]',
    '.v-overlay--active',
    '.v-overlay__content',
    '.modal-overlay',
    '.aw-editor__overlay',
].join(',');

const trustItems = [
    'Безопасная сделка',
    'Поддержка обращений',
    'Публичные правила',
];

const navigationGroups = computed<FooterGroup[]>(() => {
    const supportLinks: FooterLink[] = [
        ...(supportUserEnabled ? [{ label: 'Мои обращения', to: '/support' }] : []),
        { label: 'Написать в поддержку', href: 'mailto:support@logora.ru', emphasis: true },
        { label: 'Вопросы по персональным данным', href: 'mailto:office@logora.ru' },
        { label: 'Дмитрий в Telegram', href: 'https://t.me/logora_dmitry', external: true },
        { label: 'Максим в Telegram', href: 'https://t.me/logora_maxim', external: true },
    ];

    return [
        {
            title: 'Платформа',
            ariaLabel: 'Навигация по платформе',
            links: [
                // Оба каталога (/AllMentors и /AllEvents) на запуске закрыты
                // admin-гейтом на роутах — ссылки показываем только админам.
                ...(props.isAdmin
                    ? [
                        { label: 'Все наставники', to: '/AllMentors' },
                        { label: 'Сессии', to: '/AllEvents' },
                    ]
                    : []),
                { label: 'Войти', to: '/login' },
                { label: 'Регистрация', to: '/register' },
            ],
        },
        {
            title: 'Наставникам',
            ariaLabel: 'Разделы для наставников',
            links: [
                { label: 'Стать наставником', to: '/become-mentor' },
                { label: 'Планирование событий', to: '/CreateEntityPage' },
                { label: 'Календарь', to: '/CalendarPage' },
            ],
        },
        {
            title: 'Поддержка',
            ariaLabel: 'Поддержка пользователей',
            links: supportLinks,
        },
        {
            title: 'Документы',
            ariaLabel: 'Юридические документы',
            links: [
                { label: 'Политика обработки персональных данных', to: '/legal/privacy-policy' },
                { label: 'Публичная оферта', to: '/legal/public-offer' },
                { label: 'Оферта безопасной сделки', to: '/legal/secure-deal-offer' },
            ],
        },
    ];
});

const scrollToTop = () => {
    const prefersReducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    closeFooterReveal();
    window.scrollTo({ top: 0, behavior: prefersReducedMotion ? 'auto' : 'smooth' });
};

const getMeasuredFooterHeight = () => {
    return footerRef.value?.offsetHeight || window.innerHeight || footerHeight.value;
};

const syncFooterHeight = () => {
    footerHeight.value = getMeasuredFooterHeight();
    updateFooterReveal();
};

const updateFooterReveal = () => {
    if (!footerRef.value) return;

    const footer = footerRef.value;
    const height = getMeasuredFooterHeight();
    const progress = revealProgress.value;
    const hiddenOffset = Math.max(0, height - getFooterPeekHeight());
    const offset = Math.round((1 - progress) * hiddenOffset);

    footerHeight.value = height;
    revealActive.value = progress > 0.01;
    footer.style.setProperty('--site-footer-offset', `${offset}px`);
};

const scheduleFooterRevealUpdate = () => {
    if (animationFrame) return;
    animationFrame = window.requestAnimationFrame(() => {
        animationFrame = 0;
        updateFooterReveal();
    });
};

const getTriggerScrollY = () => {
    const scrollElement = document.scrollingElement || document.documentElement;
    return Math.max(0, scrollElement.scrollHeight - window.innerHeight);
};

const getCurrentScrollY = () => {
    const scrollElement = document.scrollingElement || document.documentElement;
    return scrollElement.scrollTop || window.scrollY || window.pageYOffset || 0;
};

const clampRevealProgress = (value: number) => Math.min(1, Math.max(0, value));

const clearFooterStopNudge = () => {
    if (stopNudgeFrame) {
        window.cancelAnimationFrame(stopNudgeFrame);
        stopNudgeFrame = 0;
    }
    if (stopNudgeTimeout) {
        window.clearTimeout(stopNudgeTimeout);
        stopNudgeTimeout = 0;
    }
    stopNudgeActive.value = false;
};

const triggerFooterStopNudge = () => {
    clearFooterStopNudge();
    stopNudgeFrame = window.requestAnimationFrame(() => {
        stopNudgeFrame = 0;
        stopNudgeActive.value = true;
        stopNudgeTimeout = window.setTimeout(() => {
            stopNudgeTimeout = 0;
            stopNudgeActive.value = false;
        }, STOP_NUDGE_DURATION_MS);
    });
};

const resetRevealStop = () => {
    footerRevealArmed = false;
    footerRevealReadyAt = 0;
    lastWheelDeltaAbs = 0;
    lastWheelEventAt = 0;
    touchRevealBlockedUntilRelease = false;
    clearFooterStopNudge();
};

const resetFooterScroll = () => {
    if (footerRef.value?.scrollTop) {
        footerRef.value.scrollTop = 0;
    }
};

const setRevealProgress = (value: number, immediate = false) => {
    revealProgress.value = clampRevealProgress(value);
    if (revealProgress.value > 0) {
        clearFooterStopNudge();
    }
    if (revealProgress.value < 0.99) {
        resetFooterScroll();
    }
    if (revealProgress.value <= 0) {
        resetRevealStop();
    }

    if (immediate) {
        updateFooterReveal();
        return;
    }

    scheduleFooterRevealUpdate();
};

const closeFooterReveal = () => {
    if (animationFrame) {
        window.cancelAnimationFrame(animationFrame);
        animationFrame = 0;
    }
    resetRevealStop();
    revealProgress.value = 0;
    resetFooterScroll();
    updateFooterReveal();
};

const isBodyScrollLocked = () => {
    return typeof document !== 'undefined'
        && document.body.classList.contains(BODY_SCROLL_LOCK_CLASS);
};

const syncBodyScrollLocked = () => {
    const locked = isBodyScrollLocked();
    bodyScrollLocked.value = locked;
    if (locked) {
        closeFooterReveal();
    }
};

const getEventTargetElement = (target: EventTarget | null) => {
    if (target instanceof Element) return target;
    if (target instanceof Node) return target.parentElement;
    return null;
};

const isModalEventTarget = (target: EventTarget | null) => {
    const element = getEventTargetElement(target);
    return Boolean(element?.closest(MODAL_REVEAL_BLOCK_SELECTOR));
};

const shouldIgnoreFooterRevealEvent = (target: EventTarget | null) => {
    if (!isBodyScrollLocked() && !isModalEventTarget(target)) return false;
    resetRevealStop();
    return true;
};

const isAtRevealTrigger = () => {
    return getCurrentScrollY() >= getTriggerScrollY() - REVEAL_TRIGGER_TOLERANCE_PX;
};

const wouldReachRevealTrigger = (deltaY: number) => {
    if (deltaY <= 0) return false;
    return getCurrentScrollY() + deltaY >= getTriggerScrollY() - REVEAL_TRIGGER_TOLERANCE_PX;
};

const scrollToRevealTrigger = () => {
    const triggerScrollY = getTriggerScrollY();
    if (Math.abs(getCurrentScrollY() - triggerScrollY) <= REVEAL_TRIGGER_TOLERANCE_PX) return;

    window.scrollTo({ top: triggerScrollY, behavior: 'auto' });
};

const shouldStopBeforeReveal = (deltaY: number) => {
    return deltaY > 0 && (isAtRevealTrigger() || wouldReachRevealTrigger(deltaY));
};

const getFooterPeekHeight = () => {
    const raw = window.getComputedStyle(document.documentElement)
        .getPropertyValue('--site-footer-peek-height')
        .trim();
    const parsed = Number.parseFloat(raw);

    return Number.isFinite(parsed) ? parsed : DEFAULT_FOOTER_PEEK_HEIGHT;
};

const getSmoothedRevealDelta = (deltaY: number) => {
    const height = footerHeight.value || window.innerHeight || 720;
    const maxDelta = Math.min(220, Math.max(140, height * 0.28));

    return Math.sign(deltaY) * Math.min(Math.abs(deltaY), maxDelta) * REVEAL_SCROLL_DAMPING;
};

const armFooterRevealStop = () => {
    scrollToRevealTrigger();
    footerRevealArmed = true;
    footerRevealReadyAt = window.performance.now() + KEY_TOUCH_REVEAL_LOCK_MS;
    triggerFooterStopNudge();
};

const armWheelFooterRevealStop = (deltaY: number) => {
    const now = window.performance.now();
    armFooterRevealStop();
    footerRevealReadyAt = now + WHEEL_REVEAL_LOCK_MS;
    lastWheelDeltaAbs = Math.abs(deltaY);
    lastWheelEventAt = now;
};

const isFooterRevealReady = () => {
    return footerRevealArmed && window.performance.now() >= footerRevealReadyAt;
};

const prepareFooterReveal = () => {
    scrollToRevealTrigger();
    if (isFooterRevealReady()) return true;

    if (!footerRevealArmed) {
        armFooterRevealStop();
    }

    return false;
};

const isNewWheelPush = (deltaAbs: number, now: number) => {
    if (now < footerRevealReadyAt) return false;
    if (lastWheelEventAt > 0 && now - lastWheelEventAt >= WHEEL_REVEAL_QUIET_GAP_MS) return true;
    if (deltaAbs < WHEEL_REVEAL_MIN_DELTA) return false;

    const accelerationThreshold = Math.max(
        lastWheelDeltaAbs + WHEEL_REVEAL_DELTA_ACCELERATION_PX,
        lastWheelDeltaAbs * WHEEL_REVEAL_DELTA_ACCELERATION_RATIO,
    );

    return deltaAbs >= accelerationThreshold;
};

const prepareWheelFooterReveal = (deltaY: number) => {
    scrollToRevealTrigger();
    const now = window.performance.now();
    const deltaAbs = Math.abs(deltaY);

    if (!footerRevealArmed) {
        armWheelFooterRevealStop(deltaY);
        return false;
    }

    const shouldReveal = isNewWheelPush(deltaAbs, now);
    lastWheelDeltaAbs = deltaAbs;
    lastWheelEventAt = now;

    return shouldReveal;
};

const prepareTouchFooterReveal = () => {
    scrollToRevealTrigger();
    if (isFooterRevealReady() && !touchRevealBlockedUntilRelease) return true;

    if (!footerRevealArmed) {
        armFooterRevealStop();
        touchRevealBlockedUntilRelease = true;
    }

    return false;
};

const isFooterEventTarget = (target: EventTarget | null) => {
    return target instanceof Node && Boolean(footerRef.value?.contains(target));
};

const canFooterConsumeScroll = (deltaY: number, target: EventTarget | null) => {
    const footer = footerRef.value;
    if (!footer || revealProgress.value < 0.99 || !isFooterEventTarget(target)) return false;

    const maxScrollTop = footer.scrollHeight - footer.clientHeight;
    if (maxScrollTop <= 1) return false;

    if (deltaY > 0) return footer.scrollTop < maxScrollTop - 1;
    if (deltaY < 0) return footer.scrollTop > 1;
    return false;
};

const scrollFooterByTouchDelta = (deltaY: number, target: EventTarget | null) => {
    const footer = footerRef.value;
    if (!footer || revealProgress.value < 0.99 || !isFooterEventTarget(target)) return false;

    const maxScrollTop = footer.scrollHeight - footer.clientHeight;
    if (maxScrollTop <= 1 || deltaY === 0) return false;

    const nextScrollTop = Math.min(maxScrollTop, Math.max(0, footer.scrollTop + deltaY));
    if (Math.abs(nextScrollTop - footer.scrollTop) < 0.5) return false;

    footer.scrollTop = nextScrollTop;
    return true;
};

const isInteractiveKeyTarget = (target: EventTarget | null) => {
    if (!(target instanceof HTMLElement)) return false;
    return Boolean(target.closest('a, button, input, textarea, select, summary, [contenteditable="true"]'));
};

const handleRevealDelta = (deltaY: number) => {
    const height = footerHeight.value || window.innerHeight || 1;
    const nextProgress = clampRevealProgress(revealProgress.value + deltaY / height);
    setRevealProgress(nextProgress);
};

const handleWheelReveal = (event: WheelEvent) => {
    if (shouldIgnoreFooterRevealEvent(event.target)) return;

    const isRevealing = revealProgress.value > 0;
    if (!isRevealing && event.deltaY < 0) {
        resetRevealStop();
        return;
    }

    if (!isRevealing && !shouldStopBeforeReveal(event.deltaY)) return;
    if (canFooterConsumeScroll(event.deltaY, event.target)) return;

    event.preventDefault();

    if (!isRevealing && !prepareWheelFooterReveal(event.deltaY)) {
        return;
    }

    const revealDelta = getSmoothedRevealDelta(event.deltaY);
    if (revealDelta) {
        handleRevealDelta(revealDelta);
    }
};

const handleTouchStartReveal = (event: TouchEvent) => {
    if (shouldIgnoreFooterRevealEvent(event.target)) {
        lastTouchY = 0;
        return;
    }

    lastTouchY = event.touches[0]?.clientY ?? 0;
    touchRevealBlockedUntilRelease = false;
    if (!isAtRevealTrigger() && revealProgress.value <= 0) {
        resetRevealStop();
    }
};

const handleTouchEndReveal = () => {
    touchRevealBlockedUntilRelease = false;
};

const handleTouchMoveReveal = (event: TouchEvent) => {
    if (shouldIgnoreFooterRevealEvent(event.target)) return;

    const currentY = event.touches[0]?.clientY ?? lastTouchY;
    const deltaY = lastTouchY - currentY;
    lastTouchY = currentY;

    // Когда футер полностью раскрыт и касание внутри — не перехватываем,
    // чтобы iOS мог применить нативную инерцию при скролле
    if (revealProgress.value >= 0.99 && isFooterEventTarget(event.target)) {
        return;
    }

    const isRevealing = revealProgress.value > 0;
    if (!isRevealing && deltaY < 0) {
        resetRevealStop();
        return;
    }

    if (scrollFooterByTouchDelta(deltaY, event.target)) {
        event.preventDefault();
        return;
    }

    if (!isRevealing && !shouldStopBeforeReveal(deltaY)) return;
    if (canFooterConsumeScroll(deltaY, event.target)) return;

    event.preventDefault();

    if (!isRevealing && !prepareTouchFooterReveal()) {
        return;
    }

    const revealDelta = getSmoothedRevealDelta(deltaY);
    if (revealDelta) {
        handleRevealDelta(revealDelta);
    }
};

const handleKeyReveal = (event: KeyboardEvent) => {
    if (shouldIgnoreFooterRevealEvent(event.target)) return;

    const keyDeltas: Record<string, number> = {
        ArrowDown: 56,
        PageDown: window.innerHeight * 0.72,
        Space: window.innerHeight * 0.72,
        ArrowUp: -56,
        PageUp: window.innerHeight * -0.72,
    };
    const delta = keyDeltas[event.code] ?? keyDeltas[event.key];
    if (!delta) return;
    if ((event.code === 'Space' || event.key === ' ') && isInteractiveKeyTarget(event.target)) return;

    const isRevealing = revealProgress.value > 0;
    if (!isRevealing && delta < 0) {
        resetRevealStop();
        return;
    }
    if (!isRevealing && !shouldStopBeforeReveal(delta)) return;
    if (canFooterConsumeScroll(delta, document.activeElement)) return;

    event.preventDefault();

    if (!isRevealing && !prepareFooterReveal()) {
        return;
    }

    const revealDelta = getSmoothedRevealDelta(delta);
    if (revealDelta) {
        handleRevealDelta(revealDelta);
    }
};

const handlePointerMove = (event: PointerEvent) => {
    const target = event.currentTarget as HTMLElement | null;
    if (!target) return;

    const rect = target.getBoundingClientRect();
    target.style.setProperty('--footer-cursor-x', `${event.clientX - rect.left}px`);
    target.style.setProperty('--footer-cursor-y', `${event.clientY - rect.top}px`);
    target.style.setProperty('--footer-cursor-opacity', '1');
};

const resetPointer = (event: PointerEvent) => {
    const target = event.currentTarget as HTMLElement | null;
    target?.style.setProperty('--footer-cursor-opacity', '0');
};

onMounted(async () => {
    await nextTick();
    syncFooterHeight();
    syncBodyScrollLocked();

    resizeObserver = new ResizeObserver(syncFooterHeight);
    if (footerRef.value) {
        resizeObserver.observe(footerRef.value);
    }
    bodyClassObserver = new MutationObserver(syncBodyScrollLocked);
    bodyClassObserver.observe(document.body, { attributes: true, attributeFilter: ['class'] });

    document.addEventListener('wheel', handleWheelReveal, WHEEL_EVENT_OPTIONS);
    window.addEventListener('touchstart', handleTouchStartReveal, { passive: true });
    window.addEventListener('touchmove', handleTouchMoveReveal, { passive: false });
    window.addEventListener('touchend', handleTouchEndReveal);
    window.addEventListener('touchcancel', handleTouchEndReveal);
    window.addEventListener('keydown', handleKeyReveal);
    window.addEventListener('resize', syncFooterHeight);
});

watch(
    () => route.fullPath,
    () => {
        closeFooterReveal();
    },
);

onBeforeUnmount(() => {
    if (animationFrame) {
        window.cancelAnimationFrame(animationFrame);
    }
    resizeObserver?.disconnect();
    bodyClassObserver?.disconnect();
    document.removeEventListener('wheel', handleWheelReveal, WHEEL_EVENT_OPTIONS);
    window.removeEventListener('touchstart', handleTouchStartReveal);
    window.removeEventListener('touchmove', handleTouchMoveReveal);
    window.removeEventListener('touchend', handleTouchEndReveal);
    window.removeEventListener('touchcancel', handleTouchEndReveal);
    window.removeEventListener('keydown', handleKeyReveal);
    window.removeEventListener('resize', syncFooterHeight);
});

return (_ctx: any,_cache: any) => {
  const _component_RouterLink = _resolveComponent("RouterLink")!

  return (_openBlock(), _createElementBlock("footer", {
    ref_key: "footerRef",
    ref: footerRef,
    class: _normalizeClass(["site-footer", {
            'site-footer--active': revealActive.value,
            'site-footer--stop-nudge': stopNudgeActive.value,
            'site-footer--body-locked': bodyScrollLocked.value,
        }]),
    "aria-labelledby": "site-footer-title",
    onPointermove: handlePointerMove,
    onPointerleave: resetPointer
  }, [
    _cache[6] || (_cache[6] = _createElementVNode("div", {
      class: "site-footer__cursor-light",
      "aria-hidden": "true"
    }, null, -1)),
    _cache[7] || (_cache[7] = _createElementVNode("div", {
      class: "site-footer__visual",
      "aria-hidden": "true"
    }, [
      _createElementVNode("svg", {
        viewBox: "0 0 720 260",
        preserveAspectRatio: "none",
        focusable: "false"
      }, [
        _createElementVNode("path", {
          class: "site-footer__wave",
          d: "M18 180C116 87 214 87 312 180C410 273 508 273 702 74",
          pathLength: "1"
        }),
        _createElementVNode("path", {
          class: "site-footer__wave site-footer__wave--secondary",
          d: "M48 78C128 146 222 154 330 100C438 46 540 50 672 138",
          pathLength: "1"
        }),
        _createElementVNode("circle", {
          cx: "110",
          cy: "104",
          r: "5"
        }),
        _createElementVNode("circle", {
          cx: "312",
          cy: "180",
          r: "5"
        }),
        _createElementVNode("circle", {
          cx: "504",
          cy: "180",
          r: "5"
        }),
        _createElementVNode("circle", {
          cx: "626",
          cy: "108",
          r: "5"
        })
      ])
    ], -1)),
    _createElementVNode("div", _hoisted_1, [
      _createElementVNode("div", _hoisted_2, [
        _createElementVNode("section", _hoisted_3, [
          _createVNode(_component_RouterLink, {
            class: "site-footer__logo-link",
            to: brandLink.value,
            "aria-label": "Logora"
          }, {
            default: _withCtx(() => [
              _createVNode(SvgLogo, {
                width: 164,
                title: "Logora"
              })
            ]),
            _: 1
          }, 8, ["to"]),
          _cache[0] || (_cache[0] = _createElementVNode("p", {
            id: "site-footer-title",
            class: "site-footer__title"
          }, " Платформа для личных сессий, наставничества и экспертных созвонов. ", -1))
        ]),
        _createElementVNode("aside", _hoisted_4, [
          _cache[1] || (_cache[1] = _createElementVNode("p", { class: "site-footer__aside-label" }, "Сервис", -1)),
          _createElementVNode("ul", _hoisted_5, [
            (_openBlock(), _createElementBlock(_Fragment, null, _renderList(trustItems, (item) => {
              return _createElementVNode("li", { key: item }, _toDisplayString(item), 1)
            }), 64))
          ])
        ])
      ]),
      _createElementVNode("div", _hoisted_6, [
        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(navigationGroups.value, (group) => {
          return (_openBlock(), _createElementBlock("nav", {
            key: group.title,
            class: "site-footer__nav",
            "aria-label": group.ariaLabel
          }, [
            _createElementVNode("details", _hoisted_8, [
              _createElementVNode("summary", _hoisted_9, _toDisplayString(group.title), 1),
              _createElementVNode("ul", _hoisted_10, [
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(group.links, (link) => {
                  return (_openBlock(), _createElementBlock("li", {
                    key: link.label
                  }, [
                    (link.to)
                      ? (_openBlock(), _createBlock(_component_RouterLink, {
                          key: 0,
                          class: _normalizeClass(["site-footer__link", { 'site-footer__link--cta': link.emphasis }]),
                          to: link.to
                        }, {
                          default: _withCtx(() => [
                            _createTextVNode(_toDisplayString(link.label), 1)
                          ]),
                          _: 2
                        }, 1032, ["class", "to"]))
                      : (_openBlock(), _createElementBlock("a", {
                          key: 1,
                          class: _normalizeClass(["site-footer__link", { 'site-footer__link--cta': link.emphasis }]),
                          href: link.href,
                          target: link.external ? '_blank' : undefined,
                          rel: link.external ? 'noopener noreferrer' : undefined
                        }, _toDisplayString(link.label), 11, _hoisted_11))
                  ]))
                }), 128))
              ])
            ])
          ], 8, _hoisted_7))
        }), 128))
      ]),
      _cache[5] || (_cache[5] = _createStaticVNode("<section class=\"site-footer__contact\" aria-label=\"Контакты и реквизиты\" data-v-3272582f><div data-v-3272582f><h3 class=\"site-footer__contact-title\" data-v-3272582f>Поддержка и документы</h3><p class=\"site-footer__contact-text\" data-v-3272582f> По вопросам сервиса, платежей, возвратов и обработки персональных данных пишите в поддержку. </p></div><div class=\"site-footer__contact-links\" data-v-3272582f><a class=\"site-footer__contact-link\" href=\"mailto:support@logora.ru\" data-v-3272582f> support@logora.ru </a><a class=\"site-footer__contact-link\" href=\"mailto:office@logora.ru\" data-v-3272582f> office@logora.ru </a></div></section>", 1)),
      _createElementVNode("div", _hoisted_12, [
        _createElementVNode("div", _hoisted_13, [
          _createElementVNode("span", null, "© " + _toDisplayString(currentYear.value) + " Logora", 1),
          _cache[2] || (_cache[2] = _createElementVNode("span", null, "ООО «ЛОГОРА», ОГРН 1267700087445, ИНН 9718294498", -1)),
          _cache[3] || (_cache[3] = _createElementVNode("span", null, "107564, г. Москва, ул. Миллионная, д. 3, к. 1, кв. 88", -1))
        ]),
        _createElementVNode("button", {
          class: "site-footer__to-top",
          type: "button",
          onClick: scrollToTop
        }, _cache[4] || (_cache[4] = [
          _createElementVNode("span", null, "Наверх", -1),
          _createElementVNode("svg", {
            width: "16",
            height: "16",
            viewBox: "0 0 16 16",
            "aria-hidden": "true",
            focusable: "false"
          }, [
            _createElementVNode("path", { d: "M8 3.5L3.75 7.75M8 3.5L12.25 7.75M8 3.5V13" })
          ], -1)
        ]))
      ])
    ])
  ], 34))
}
}

})